import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Space,
  Table,
  Tag,
  Select,
  Modal,
  Descriptions,
  Typography,
  Button,
  Avatar,
  Statistic,
  Tooltip,
  Grid,
} from "antd";
import {
  PhoneOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  ToolOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  TeamOutlined,
  FileExcelOutlined, // Ícone para Excel
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../api/Api";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";          // Para gerar Excel
import { saveAs } from "file-saver";   // Para salvar o arquivo

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ListagemChamadasRegistadas = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const inicioMes = dayjs().startOf("month");
  const fimMes = dayjs().endOf("month");

  const [datas, setDatas] = useState([inicioMes, fimMes]);
  const [loading, setLoading] = useState(false);
  const [chamadas, setChamadas] = useState([]);
  const [tipoFiltro, setTipoFiltro] = useState(null);
  const [provinciaFiltro, setProvinciaFiltro] = useState(null);
  const [detalheVisible, setDetalheVisible] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const [totalChamadas, setTotalChamadas] = useState(0);
  const [totalInfo, setTotalInfo] = useState(0);
  const [totalSolicitacao, setTotalSolicitacao] = useState(0);
  const [totalReclamacao, setTotalReclamacao] = useState(0);
  const [totalIndisponibilidade, setTotalIndisponibilidade] = useState(0);

  useEffect(() => {
    if (datas && datas[0] && datas[1]) {
      fetchChamadas();
      fetchDashboard();
    }
  }, [datas]);

  const fetchChamadas = async () => {
    try {
      setLoading(true);
      const payload = { dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD") };
      const response = await api.ListChamadaRegistadaWhatsApp(payload);
      if (Array.isArray(response)) setChamadas(response);
    } catch (error) {
      console.error("Erro ao buscar chamadas registadas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const payload = { dataInicio: datas[0].format("YYYY-MM-DD"), dataFim: datas[1].format("YYYY-MM-DD") };
      const response = await api.ListDashboardChamadaRegistadaWhatsApp(payload);
      if (response && typeof response === "object") {
        setTotalChamadas(response.total);
        setTotalInfo(response.informacao);
        setTotalSolicitacao(response.solicitacao);
        setTotalReclamacao(response.reclamacao);
        setTotalIndisponibilidade(response.indisponibilidde);
      }
    } catch (error) {
      console.error("Erro ao buscar dashboard:", error);
    }
  };

  const filteredChamadas = chamadas.filter((c) =>
    (!tipoFiltro || c.tipo_chamada === tipoFiltro) &&
    (!provinciaFiltro || c.provincia === provinciaFiltro)
  );

  // Exportar para PDF (mantido igual)
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    doc.setFontSize(20); doc.setTextColor(44, 62, 80);
    doc.text("RELATÓRIO DE INTERAÇÕES DO WHATSAPP", pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(12); doc.setTextColor(100, 100, 100);
    doc.text(`Período: ${datas[0].format("DD/MM/YYYY")} - ${datas[1].format("DD/MM/YYYY")}`, margin, 35);
    doc.text(`Data de geração: ${dayjs().format("DD/MM/YYYY HH:mm")}`, margin, 42);
    const statsData = [
      ["Total de Chamadas:", totalChamadas], ["Informações:", totalInfo],
      ["Reclamações:", totalReclamacao], ["Solicitações:", totalSolicitacao],
    
    ];
    let y = 60;
    statsData.forEach(([label, value]) => { doc.text(`${label} ${value}`, margin, y); y += 6; });
    if (filteredChamadas.length > 0) {
      doc.autoTable({
        startY: y + 10,
        head: [["#", "Nome", "Província", "Município", "Data", "Tipo", "Ocorrência"]],
        body: filteredChamadas.map((c, i) => [i + 1, c.nome || "N/A", c.provincia || "N/A", c.municipio || "N/A", dayjs(c.data).format("DD/MM/YYYY HH:mm"), c.tipo_chamada || "N/A", (c.ocorrencia || "N/A").substring(0, 50)]),
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });
    }
    doc.save(`chamadas_registadas_${dayjs().format("YYYY-MM-DD_HH-mm")}.pdf`);
  };

  // NOVO: Exportar para Excel com todas as informações do modal
  const exportToExcel = () => {
    // Prepara os dados no formato adequado para a planilha
    const dados = filteredChamadas.map((c) => ({
      ID: c.id,
      Telefone: c.telefone || "",
      Nome: c.nome || "",
      Província: c.provincia || "",
      Município: c.municipio || "",
      Tipo: c.tipo_chamada || "",
      Data: dayjs(c.data).format("DD/MM/YYYY HH:mm"),
      Ocorrência: c.ocorrencia || "",
      Observação: c.obs || "",
      Plano: c.plano || "",
      Pacote: c.pacote || "",
    }));

    // Cria a planilha e o workbook
    const worksheet = XLSX.utils.json_to_sheet(dados);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chamadas");

    // Gera o arquivo Excel e dispara o download
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `chamadas_registadas_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`);
  };

  const getTipoIcon = (tipo) => {
    const icons = { Informação: <InfoCircleOutlined />, Reclamação: <ExclamationCircleOutlined />, Solicitação: <ToolOutlined />, Indisponibilidade: <CloseCircleOutlined /> };
    return icons[tipo] || <PhoneOutlined />;
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      width: 180,
      render: (text) => (
        <Space>
          <Avatar size="small" icon={<PhoneOutlined />} style={{ backgroundColor: "#1890ff" }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    ...(!isMobile ? [
      { title: "Província", dataIndex: "provincia", key: "provincia", width: 120, render: (text) => <Text>{text || "—"}</Text> },
      { title: "Município", dataIndex: "municipio", key: "municipio", width: 120, render: (text) => <Text>{text || "—"}</Text> },
    ] : []),
    {
      title: "Data",
      dataIndex: "data",
      key: "data",
      width: 130,
      render: (text) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(text).format("DD/MM/YYYY")}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(text).format("HH:mm")}</Text>
        </Space>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "tipo_chamada",
      key: "tipo_chamada",
      width: 150,
      render: (tipo) => {
        const cores = { Reclamação: "volcano", Solicitação: "blue", Informação: "green", Indisponibilidade: "purple" };
        return (
          <Tag color={cores[tipo]} icon={getTipoIcon(tipo)} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: isMobile ? 11 : 13 }}>
            {tipo}
          </Tag>
        );
      },
    },
    {
      title: "Ação",
      key: "acao",
      width: 90,
      align: "center",
      render: (_, record) => (
        <Button type="link" onClick={() => { setDetalhe(record); setDetalheVisible(true); }} style={{ padding: 0, fontSize: isMobile ? 12 : 14 }}>
          Detalhes
        </Button>
      ),
    },
  ];

  const stats = useMemo(() => [
    { title: "Total", value: totalChamadas || 0, color: "#1890ff", icon: <PhoneOutlined /> },
    { title: "Informações", value: totalInfo || 0, color: "#52c41a", icon: <InfoCircleOutlined /> },
    { title: "Reclamações", value: totalReclamacao || 0, color: "#ff4d4f", icon: <ExclamationCircleOutlined /> },
    { title: "Solicitações", value: totalSolicitacao || 0, color: "#faad14", icon: <ToolOutlined /> },
    
  ], [totalChamadas, totalInfo, totalReclamacao, totalSolicitacao, totalIndisponibilidade]);

  const p = isMobile ? "12px 8px" : isTablet ? "20px 16px" : "32px 48px";

  return (
    <div style={{ padding: p, minHeight: "100vh", overflowY: "auto", boxSizing: "border-box", background: "linear-gradient(145deg, #f0f2f5 0%, #f8f9fa 100%)" }}>

      {/* Cabeçalho */}
      <div style={{ marginBottom: 24, padding: isMobile ? 16 : "24px 32px", background: "linear-gradient(135deg, rgba(24,144,255,0.08) 0%, rgba(24,144,255,0.02) 100%)", borderRadius: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid rgba(24,144,255,0.1)" }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} xl={10}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar size={isMobile ? 48 : 64} icon={<TeamOutlined />} style={{ backgroundColor: "#1890ff", boxShadow: "0 4px 12px rgba(24,144,255,0.3)", flexShrink: 0 }} />
              <div>
                <Title level={isMobile ? 4 : 2} style={{ margin: 0, color: "#1a3353" }}>Interações do WhatsApp</Title>
                {!isMobile && <Text type="secondary" style={{ fontSize: 16 }}>Gerencie e visualize todas as interações do WhatsApp</Text>}
              </div>
            </div>
          </Col>
          <Col xs={24} xl={14}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Período</Text>
                <RangePicker format="DD/MM/YYYY" value={datas} onChange={(values) => setDatas(values)} style={{ borderRadius: 10, width: "100%" }} size={isMobile ? "middle" : "large"} />
              </Col>
              <Col xs={12} sm={8}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Tipo</Text>
                <Select placeholder="Filtrar por tipo" allowClear onChange={(value) => setTipoFiltro(value)} style={{ width: "100%" }} size={isMobile ? "middle" : "large"}>
                  <Option value="Reclamação">Reclamação</Option>
                  <Option value="Solicitação">Solicitação</Option>
                  <Option value="Informação">Informação</Option>
                  
                </Select>
              </Col>
              <Col xs={12} sm={8}>
                <Text strong style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Província</Text>
                <Select placeholder="Filtrar por província" allowClear onChange={(value) => setProvinciaFiltro(value)} style={{ width: "100%" }} size={isMobile ? "middle" : "large"}>
                  {[...new Set(chamadas.map((c) => c.provincia))].map((prov, i) => (
                    <Option key={i} value={prov}>{prov}</Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Cards de resumo */}
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        {stats.map((item, index) => (
          <Col xs={12} sm={8} md={8} lg={4} key={index}>
            <Card bordered={false} style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }} hoverable bodyStyle={{ padding: isMobile ? "14px" : "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 16 }}>
                <Avatar size={isMobile ? 36 : 48} icon={item.icon} style={{ backgroundColor: item.color, boxShadow: `0 4px 12px ${item.color}40`, flexShrink: 0 }} />
                <Statistic
                  title={<Text type="secondary" style={{ fontSize: isMobile ? 11 : 13 }}>{item.title}</Text>}
                  value={item.value}
                  valueStyle={{ color: "#1a3353", fontWeight: 600, fontSize: isMobile ? 18 : 24 }}
                />
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Tabela */}
      <Card bordered={false} style={{ borderRadius: 20, boxShadow: "0 8px 24px rgba(0,0,0,0.08)", overflow: "hidden" }} bodyStyle={{ padding: 0 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <Title level={4} style={{ margin: 0, fontSize: isMobile ? 15 : 18 }}>Lista de Chamadas</Title>
            <Space size="small" wrap>
              <Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 20 }}>Total: {filteredChamadas.length}</Tag>
              {/* Botão para Excel (novo) */}
              <Tooltip title="Exportar Excel">
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  onClick={exportToExcel}
                  size={isMobile ? "small" : "middle"}
                  style={{ backgroundColor: "#28a745", borderColor: "#28a745", borderRadius: 10, boxShadow: "0 4px 12px rgba(40,167,69,0.3)" }}
                >
                  {!isMobile && "Excel"}
                </Button>
              </Tooltip>
              {/* Botão para PDF (já existente) */}
              <Tooltip title="Exportar PDF">
                <Button type="primary" icon={<FilePdfOutlined />} onClick={exportToPDF} size={isMobile ? "small" : "middle"}
                  style={{ backgroundColor: "#dc3545", borderColor: "#dc3545", borderRadius: 10, boxShadow: "0 4px 12px rgba(220,53,69,0.3)" }}>
                  {!isMobile && "PDF"}
                </Button>
              </Tooltip>
            </Space>
          </div>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredChamadas}
          loading={loading}
          size={isMobile ? "small" : "middle"}
          scroll={{ x: "max-content" }}
          pagination={{
            pageSize: 5,
            size: isMobile ? "small" : "default",
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            position: ["bottomCenter"],
            showTotal: (total, range) => isMobile ? `${range[0]}-${range[1]} / ${total}` : `${range[0]}-${range[1]} de ${total} chamadas`,
            style: { margin: "16px 0", paddingBottom: 8 },
          }}
          rowClassName={() => "custom-row-hover"}
        />
      </Card>

      {/* Modal de Detalhes */}
      <Modal
        open={detalheVisible}
        title={<Space><PhoneOutlined style={{ color: "#1890ff" }} /><span style={{ fontWeight: 600, fontSize: isMobile ? 16 : 18 }}>Detalhes da Chamada</span></Space>}
        footer={null}
        onCancel={() => setDetalheVisible(false)}
        width={isMobile ? "100%" : 700}
        style={isMobile ? { top: 0, margin: 0, maxWidth: "100vw" } : { top: 20 }}
        styles={isMobile ? { body: { padding: "12px 8px" }, content: { borderRadius: 0 } } : {}}
        centered={!isMobile}
      >
        {detalhe && (
          <Descriptions bordered column={1} size={isMobile ? "small" : "middle"}
            labelStyle={{ fontWeight: 600, background: "#fafafa", width: isMobile ? "35%" : "140px" }}>
            <Descriptions.Item label="ID"><Text copyable>{detalhe.id}</Text></Descriptions.Item>
            <Descriptions.Item label="Nº telefone">{detalhe.telefone}</Descriptions.Item>
            <Descriptions.Item label="Nome">{detalhe.nome}</Descriptions.Item>
            <Descriptions.Item label="Província">{detalhe.provincia}</Descriptions.Item>
            <Descriptions.Item label="Município">{detalhe.municipio}</Descriptions.Item>
            <Descriptions.Item label="Tipo">
              <Tag color={{ Reclamação: "volcano", Solicitação: "blue", Informação: "green", Indisponibilidade: "purple" }[detalhe.tipo_chamada]}
                style={{ borderRadius: 12, padding: "2px 12px" }}>{detalhe.tipo_chamada}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Data">{dayjs(detalhe.data).format("DD/MM/YYYY HH:mm")}</Descriptions.Item>
            <Descriptions.Item label="Ocorrência"><div style={{ maxHeight: 120, overflowY: "auto", whiteSpace: "pre-wrap" }}>{detalhe.ocorrencia || "—"}</div></Descriptions.Item>
            <Descriptions.Item label="Observação"><div style={{ maxHeight: 120, overflowY: "auto", whiteSpace: "pre-wrap" }}>{detalhe.obs || "Nenhuma observação"}</div></Descriptions.Item>
            <Descriptions.Item label="Plano">{detalhe.plano || "—"}</Descriptions.Item>
            <Descriptions.Item label="Pacote">{detalhe.pacote || "—"}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <style>{`
        .custom-row-hover:hover { background-color: #f0f7ff !important; transition: background-color 0.2s; }
        @media (max-width: 767px) { .ant-table-pagination { justify-content: center !important; } }
      `}</style>
    </div>
  );
};

export default ListagemChamadasRegistadas;